import { create } from "zustand";
import type {
  PreflightPlacementCode,
  PreflightPlatformCode,
} from "@nexus/contracts";
import { onlyPlacementFor } from "../constants/labels";
import { parseUrlInput, type ParsedUrlInput } from "../lib/parse-urls";

export interface PreflightFileMeta {
  name: string;
  sizeBytes: number;
  lastModified: number;
  contentType: string;
}

export interface PreflightDraftState {
  /** Confirmed upload ids (direct file uploads that reached READY). */
  uploadIds: string[];
  /** File metadata keyed by uploadId — used to restore the asset list after navigation. */
  uploadMetas: Record<string, PreflightFileMeta>;
  /** Raw URL strings (stay strings until create-report materializes them — KTD4). */
  urls: string[];
  platform: PreflightPlatformCode | null;
  placement: PreflightPlacementCode | null;
  name: string;
  /** Markets the report is created in; Analyze stays disabled until at least one. */
  marketIds: string[];

  addUpload: (uploadId: string, meta: PreflightFileMeta) => void;
  removeUpload: (uploadId: string) => void;
  /**
   * Parse a pasted blob into URLs (splitting on multiple delimiters), add the
   * valid de-duplicated ones, and report what was skipped for inline feedback.
   */
  addUrlStrings: (raw: string) => ParsedUrlInput;
  removeUrl: (url: string) => void;
  /**
   * Setting a platform clears any placement that no longer applies, then selects the
   * new platform's placement outright when it only has one.
   */
  setPlatform: (platform: PreflightPlatformCode | null) => void;
  setPlacement: (placement: PreflightPlacementCode | null) => void;
  setName: (name: string) => void;
  setMarketIds: (marketIds: string[]) => void;
  clear: () => void;
  /** Bumped on every `clear()` so upload UI can reset in sync with the draft. */
  sessionEpoch: number;
}

const EMPTY_DRAFT = {
  uploadIds: [] as string[],
  uploadMetas: {} as Record<string, PreflightFileMeta>,
  urls: [] as string[],
  platform: null as PreflightPlatformCode | null,
  placement: null as PreflightPlacementCode | null,
  name: "",
  marketIds: [] as string[],
};

export const usePreflightDraftStore = create<PreflightDraftState>(
  (set, get) => ({
    ...EMPTY_DRAFT,
    sessionEpoch: 0,

    addUpload: (uploadId, meta) =>
      set((state) =>
        state.uploadIds.includes(uploadId)
          ? state
          : {
              uploadIds: [...state.uploadIds, uploadId],
              uploadMetas: { ...state.uploadMetas, [uploadId]: meta },
            },
      ),

    removeUpload: (uploadId) =>
      set((state) => {
        const { [uploadId]: _dropped, ...restMetas } = state.uploadMetas;
        return {
          uploadIds: state.uploadIds.filter((id) => id !== uploadId),
          uploadMetas: restMetas,
        };
      }),

    addUrlStrings: (raw) => {
      const state = get();
      const parsed = parseUrlInput(raw, state.urls, {
        existingAssetCount: state.uploadIds.length + state.urls.length,
      });
      if (parsed.valid.length > 0) {
        set((current) => ({ urls: [...current.urls, ...parsed.valid] }));
      }
      return parsed;
    },

    removeUrl: (url) =>
      set((state) => ({ urls: state.urls.filter((u) => u !== url) })),

    setPlatform: (platform) =>
      set({ platform, placement: platform ? onlyPlacementFor(platform) : null }),
    setPlacement: (placement) => set({ placement }),
    setName: (name) => set({ name }),
    setMarketIds: (marketIds) => set({ marketIds }),

    clear: () =>
      set({ ...EMPTY_DRAFT, sessionEpoch: get().sessionEpoch + 1 }),
  }),
);
